import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

const faqs = [
  {
    question: 'Does Bromley\'s AMBER overall DfT rating mean I cannot claim?',
    answer: 'No. AMBER is the overall scorecard — condition is RED, spend is GREEN and best practice is RED. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired, not on a headline rating alone. Bromley\'s own transparency report shows 34.3% of B, C and U roads in major deterioration in 2024/25, up from 20.6% two years earlier — context that can support a claim when combined with photos and prior reports.',
  },
  {
    question: 'Why is condition RED when spend is GREEN?',
    answer: 'They measure different things. The DfT Spend scorecard is GREEN because projected 2025/26 capital spend (£2.7m) exceeds the DfT allocation (£1.479m). The Condition scorecard is RED because published survey data shows network deterioration — especially on B, C and U roads, where major-deterioration share rose from 20.6% to 34.3% between 2022/23 and 2024/25. Higher budgets do not automatically prove the individual pothole was known and repaired within inspection intervals.',
  },
  {
    question: 'The DfT flags incomplete condition data for Bromley — what does that mean?',
    answer: 'The Department for Transport notes that Bromley\'s overall, condition and best-practice scorecards are based on incomplete road condition data. That does not invalidate the published AI survey percentages in the council\'s transparency report — but it means the DfT could not fully validate the picture. For claimants, the council\'s own published figures and inspection records for your specific road remain the primary evidence.',
  },
  {
    question: 'Why did Bromley allocate no surfacing budget for 2021 to 2023?',
    answer: 'Bromley\'s transparency report states: "This programme brought forward approximately £10 million in revenue funding to take advantage of lower contract rates at the time. Consequently, the council did not allocate a surfacing budget for the 2021 to 2023 period." Capital spend that year was £200k with 0 miles resurfaced and just 9% spent on preventative maintenance. B, C and U-road major deterioration then rose from 20.6% to 33.3% before reaching 34.3% in 2024/25.',
  },
  {
    question: 'Why did pothole repairs fall from 4,121 to 2,117 in 2024/25?',
    answer: 'The council states: "The number of potholes fell in 2024/2025 as the council had to raise more jobs for larger areas which would not be classed as smaller than 1 square metre or a pothole." Bromley defines a pothole as a reactive repair or patch under 1 square metre. Fewer counted potholes does not necessarily mean fewer defects — larger failure zones may be reclassified. The council still expects 3,000–4,000 pothole fixes in 2025/26.',
  },
  {
    question: 'What if my pothole was on a residential or unclassified road?',
    answer: 'B, C and U roads make up 507.4 miles — 92% of Bromley\'s 551-mile carriageway network. These roads are surveyed annually using AI-led models. At the 2024/25 survey, 34.3% were in major deterioration and just 36.7% required no further investigation — down from 54.6% two years earlier. If your incident was on a residential street, the council\'s own condition data shows widespread deterioration on the road class where most claims occur.',
  },
  {
    question: 'How do I report a pothole to Bromley Council?',
    answer: 'Report road and pavement defects via Bromley\'s FixMyStreet portal at fix.bromley.gov.uk — enter a postcode or street name, pin the location on the map, and submit details. For defects posing an immediate danger (such as a large or deep pothole), call the out-of-hours team on 0300 303 8671 rather than using the online form. Prior reports of the same defect strengthen a claim by demonstrating the council had notice before your incident. Fixtyer searches for existing reports and attaches them to your claim pack.',
  },
];

export function BromleyCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Bromley Pothole Claims | 34.3% BCU Roads Major Deterioration — DfT RED Condition | Fixtyer"
        description="Bromley Council earns RED on DfT condition, GREEN on spend. After zero resurfacing budget 2021–23, B/C/U roads hit 34.3% major deterioration. Pothole count fell to 2,117 as defects exceeded 1m². What the published data means for your claim."
        canonical="/councils/bromley"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Bromley', url: '/councils/bromley' },
        ]}
      />
      <FAQSchema faqs={faqs} />
      <ServiceSchema
        councilName="Bromley Council"
        councilSlug="bromley"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/bromley"
        headline="Bromley Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Bromley</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="red" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="red" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <div className="bg-rose-500/10 border border-rose-500/30 rounded-xl px-4 py-3 mb-6 max-w-3xl">
            <p className="text-rose-200 text-sm">
              <strong className="text-rose-400">DfT caveat:</strong> The Department for Transport notes that Bromley's
              overall, condition and best-practice scorecards are based on{' '}
              <strong className="text-white">incomplete road condition data</strong>.
            </p>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            34.3% of Bromley's BCU Roads in Major Deterioration
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            London Borough of Bromley maintains 551 miles of roads. After allocating{' '}
            <strong className="text-rose-400">no surfacing budget for 2021 to 2023</strong>, B, C and U-road major
            deterioration rose from 20.6% to 34.3% — a 67% relative increase. The DfT rates condition{' '}
            <strong className="text-rose-400">RED</strong> and spend <strong className="text-emerald-400">GREEN</strong>.
            Pothole repairs fell to 2,117 in 2024/25 because defects exceeded the council's 1m² definition. Section 58
            still turns on your specific defect.
          </p>

          <div className="bg-rose-500/20 border border-rose-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-rose-400">9%</div>
            <div className="text-white font-medium mt-2">Preventative spend in 2021/22</div>
            <div className="text-brand-300 text-sm mt-1">
              91% reactive — 0 miles resurfaced — after a £10m front-loaded capital programme meant no surfacing budget
              was allocated for two years. Major deterioration on residential roads then accelerated sharply.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* Network scale */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            551 Miles of Roads — Mostly Residential
          </h2>
          <p className="text-surface-600 mb-8">
            Network scale from Bromley's 2025 transparency report — where pothole claims actually happen
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">551 miles</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Total carriageway</div>
              <div className="text-xs text-surface-500 mt-1">886.8 km</div>
            </div>
            <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-rose-600">507.4 miles</div>
              <div className="text-sm text-rose-800 font-medium mt-1">B, C and U roads</div>
              <div className="text-xs text-rose-700 mt-1">92% of the network</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">43.6 miles</div>
              <div className="text-sm text-surface-700 font-medium mt-1">A roads (principal)</div>
              <div className="text-xs text-surface-500 mt-1">TfL LoHEG BPRN surveys</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">885.3 miles</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Footways</div>
              <div className="text-xs text-surface-500 mt-1">1,424.8 km</div>
            </div>
          </div>

          <QuoteCard
            quote="B and C roads connect the principal roads with the unclassified U Roads which are mostly residential quieter roads."
            source="London Borough of Bromley Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* Condition data */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What RED Condition Actually Shows
          </h2>
          <p className="text-surface-600 mb-8">
            AI-led surveys from 2022 — three condition categories per the council's transparency report
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
            <p className="text-amber-800 text-sm">
              <strong>Survey caveat:</strong> Bromley uses AI-led condition surveys on principal roads via TfL's
              Borough Principal Road Network programme, and the same methodology on B, C and U roads. No condition
              surveys were undertaken in 2020 and 2021 due to Covid-19 restrictions. The DfT separately notes that
              Bromley's scorecards are based on incomplete road condition data — read published percentages as the
              council's own survey snapshot, not a guarantee of current street-level condition.
            </p>
          </div>

          {/* A roads */}
          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A roads (43.6 miles) — surveyed annually via TfL BPRN</h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Major deterioration</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Minor deterioration</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">No further investigation</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022/23</td>
                    <td className="px-4 py-2 text-center">31.60%</td>
                    <td className="px-4 py-2 text-center">23.80%</td>
                    <td className="px-4 py-2 text-center">44.60%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023/24</td>
                    <td className="px-4 py-2 text-center">31.40%</td>
                    <td className="px-4 py-2 text-center">24.60%</td>
                    <td className="px-4 py-2 text-center">44.00%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024/25</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">28.80%</td>
                    <td className="px-4 py-2 text-center font-bold">24.60%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">46.60%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              Principal roads are in a similar state of disrepair to the London average of 29% in major deterioration,
              with less than half in good condition and nearly a third needing immediate maintenance — per the council's
              own report.
            </p>
          </div>

          {/* BCU roads */}
          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 mb-8">
            <h3 className="font-semibold text-rose-900 mb-4">B, C and U roads (507.4 miles) — where most claims start</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-rose-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-rose-100 border-b border-rose-200">
                    <th className="px-4 py-2 text-left font-semibold text-rose-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Major deterioration</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Minor deterioration</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">No further investigation</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2021/22</td>
                    <td className="px-4 py-2 text-center">23.60%</td>
                    <td className="px-4 py-2 text-center">25.80%</td>
                    <td className="px-4 py-2 text-center">50.60%</td>
                  </tr>
                  <tr className="border-b border-rose-100 bg-emerald-50">
                    <td className="px-4 py-2 font-mono">2022/23</td>
                    <td className="px-4 py-2 text-center text-emerald-700">20.60%</td>
                    <td className="px-4 py-2 text-center">24.80%</td>
                    <td className="px-4 py-2 text-center text-emerald-700">54.60%</td>
                  </tr>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2023/24</td>
                    <td className="px-4 py-2 text-center text-rose-600 font-semibold">33.30%</td>
                    <td className="px-4 py-2 text-center">33.30%</td>
                    <td className="px-4 py-2 text-center">33.40%</td>
                  </tr>
                  <tr className="bg-rose-50">
                    <td className="px-4 py-2 font-mono font-bold">2024/25</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">34.30%</td>
                    <td className="px-4 py-2 text-center font-bold">29.00%</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">36.70%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-rose-800 font-medium">
              The non-principal network has continued to deteriorate after 2022/23 — major deterioration rose from
              20.6% to 34.3% in two years (+67% relative). Roads requiring no further investigation fell from 54.6%
              to 36.7%. At 34.3%, roughly 174 miles of the 507.4-mile BCU network are in the major-deterioration
              category at the last survey.
            </p>
          </div>

          <QuoteCard
            quote="As seen in the table below, the non-principal network has continued to deteriorate after 2022/2023."
            source="London Borough of Bromley Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* The £10m programme */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Zero Resurfacing Budget for Two Years
          </h2>
          <p className="text-surface-600 mb-8">
            The council's own explanation for 2021/22 and 2022/23 — and the spending that followed
          </p>

          <QuoteCard
            quote="The low levels of planned surfacing activity in 2021/22 and 2022/23 are a result of a capital programme launched in 2017. This programme brought forward approximately £10 million in revenue funding to take advantage of lower contract rates at the time. Consequently, the council did not allocate a surfacing budget for the 2021 to 2023 period."
            source="London Borough of Bromley Local Highways Maintenance Transparency Report 2025"
          />

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mt-8 mb-6">
            <table className="w-full text-sm">
              <caption className="sr-only">Bromley prevention and reactive spending 2020-2026</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Capital spend (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Resurfaced (miles)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Reactive</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-100">
                <tr className="bg-emerald-50">
                  <td className="px-4 py-3 font-medium">2025/26 (projected)</td>
                  <td className="px-4 py-3 text-center">2,700</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-600">11.8</td>
                  <td className="px-4 py-3 text-center text-emerald-600">54%</td>
                  <td className="px-4 py-3 text-center">46%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2024/25</td>
                  <td className="px-4 py-3 text-center">2,500</td>
                  <td className="px-4 py-3 text-center">6.6</td>
                  <td className="px-4 py-3 text-center">48%</td>
                  <td className="px-4 py-3 text-center text-amber-600">52%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2023/24</td>
                  <td className="px-4 py-3 text-center">2,500</td>
                  <td className="px-4 py-3 text-center">5.76</td>
                  <td className="px-4 py-3 text-center">54%</td>
                  <td className="px-4 py-3 text-center">46%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2022/23</td>
                  <td className="px-4 py-3 text-center">2,438</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-600">0</td>
                  <td className="px-4 py-3 text-center">64%</td>
                  <td className="px-4 py-3 text-center">36%</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-4 py-3 font-bold">2021/22</td>
                  <td className="px-4 py-3 text-center font-bold">200</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-600">0</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-600">9%</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-600">91%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2020/21</td>
                  <td className="px-4 py-3 text-center">2,000</td>
                  <td className="px-4 py-3 text-center">8.0</td>
                  <td className="px-4 py-3 text-center">55%</td>
                  <td className="px-4 py-3 text-center">45%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-rose-50 rounded-xl p-6 border border-rose-200">
              <h3 className="font-semibold text-rose-900 mb-3">What the data shows</h3>
              <p className="text-rose-800 text-sm">
                Capital spend collapsed to £200k in 2021/22 with zero miles resurfaced and a record-low 9% preventative
                share. The BCU major-deterioration rate then jumped from 20.6% to 33.3% between 2022/23 and 2023/24 —
                coinciding with the period when no surfacing budget was allocated.
              </p>
            </div>
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Coverage maths for 2025/26</h3>
              <p className="text-surface-600 text-sm">
                At the planned 11.8 miles of resurfacing, Bromley would treat roughly 2.1% of its 551-mile network in
                one year. Applying major-deterioration rates to network length suggests around 186.6 miles currently
                need maintenance (43.6 × 28.8% on A roads plus 507.4 × 34.3% on BCU roads) — a backlog that would
                take ~16 years to clear at the current resurfacing rate, before accounting for new deterioration.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Pothole Counts and the 1m² Definition
          </h2>
          <p className="text-surface-600 mb-8">
            Estimated potholes filled — defects the council defines as reactive repairs under 1 square metre
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
            <p className="text-amber-800 text-sm">
              <strong>Definition caveat:</strong> Bromley states that "in engineering terms, a pothole has no official
              definition, but in our case, we have defined it as a reactive repair or a patch that is under 1 square
              metres." Defects above that threshold are counted differently — which affects year-on-year comparisons.
            </p>
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <caption className="sr-only">Bromley estimated potholes filled 2021-2025</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Change vs 2021/22</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">2,790</td>
                  <td className="px-6 py-4 text-center text-surface-500">Baseline</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">3,490</td>
                  <td className="px-6 py-4 text-center text-amber-600">+25.1%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">4,121</td>
                  <td className="px-6 py-4 text-center text-rose-600">+47.7%</td>
                </tr>
                <tr className="bg-blue-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-blue-700">2,117</td>
                  <td className="px-6 py-4 text-center font-bold text-blue-700">−24.1%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">2025/26 (projected)</td>
                  <td className="px-6 py-4 text-center font-bold">3,000–4,000</td>
                  <td className="px-6 py-4 text-center text-surface-500">Council estimate</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <QuoteCard
              quote="The number of potholes fell in 2024/2025 as the council had to raise more jobs for larger areas which would not be classed as smaller than 1 square metre or a pothole."
              source="London Borough of Bromley Local Highways Maintenance Transparency Report 2025"
            />
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Fewer counts, not necessarily fewer defects</h3>
              <p className="text-surface-600 text-sm">
                Repairs peaked at 4,121 in 2023/24 then halved to 2,117 — not because the council admits fixing fewer
                defects overall, but because larger failure zones exceed the pothole definition. The council still
                expects 3,000–4,000 pothole fixes in 2025/26 based on historical trends.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Spending */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold mb-4">
            Following the Money
          </h2>
          <p className="text-brand-200 mb-8 max-w-2xl">
            GREEN spend — but reactive maintenance overtook prevention again in 2024/25
          </p>

          <div className="bg-brand-900/50 rounded-2xl border border-brand-800 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <caption className="sr-only">Bromley highway maintenance spending 2020-2026</caption>
              <thead>
                <tr className="bg-brand-800 border-b border-brand-700">
                  <th className="px-4 py-3 text-left font-semibold text-brand-300">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">DfT capital (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Capital spend (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Revenue spend (£000s)</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">km resurfaced</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">Reactive</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-800">
                <tr className="bg-emerald-900/30">
                  <td className="px-4 py-3 font-medium">2025/26 (projected)</td>
                  <td className="px-4 py-3 text-center">1,479</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-400">2,700</td>
                  <td className="px-4 py-3 text-center">2,300</td>
                  <td className="px-4 py-3 text-center">19.03</td>
                  <td className="px-4 py-3 text-center text-emerald-400">54%</td>
                  <td className="px-4 py-3 text-center">46%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2024/25</td>
                  <td className="px-4 py-3 text-center">455</td>
                  <td className="px-4 py-3 text-center">2,500</td>
                  <td className="px-4 py-3 text-center">2,683</td>
                  <td className="px-4 py-3 text-center">10.7</td>
                  <td className="px-4 py-3 text-center">48%</td>
                  <td className="px-4 py-3 text-center text-amber-400">52%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2023/24</td>
                  <td className="px-4 py-3 text-center">455</td>
                  <td className="px-4 py-3 text-center">2,500</td>
                  <td className="px-4 py-3 text-center">2,172</td>
                  <td className="px-4 py-3 text-center">9.27</td>
                  <td className="px-4 py-3 text-center">54%</td>
                  <td className="px-4 py-3 text-center">46%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2022/23</td>
                  <td className="px-4 py-3 text-center">—</td>
                  <td className="px-4 py-3 text-center">2,438</td>
                  <td className="px-4 py-3 text-center">1,343</td>
                  <td className="px-4 py-3 text-center">0</td>
                  <td className="px-4 py-3 text-center">64%</td>
                  <td className="px-4 py-3 text-center">36%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2021/22</td>
                  <td className="px-4 py-3 text-center">—</td>
                  <td className="px-4 py-3 text-center">200</td>
                  <td className="px-4 py-3 text-center">2,101</td>
                  <td className="px-4 py-3 text-center">0</td>
                  <td className="px-4 py-3 text-center text-rose-400">9%</td>
                  <td className="px-4 py-3 text-center text-rose-400">91%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">2020/21</td>
                  <td className="px-4 py-3 text-center">—</td>
                  <td className="px-4 py-3 text-center">2,000</td>
                  <td className="px-4 py-3 text-center">1,642</td>
                  <td className="px-4 py-3 text-center">12.9</td>
                  <td className="px-4 py-3 text-center">55%</td>
                  <td className="px-4 py-3 text-center">45%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-emerald-500/20 rounded-xl p-6 border border-emerald-500/30">
              <h3 className="font-semibold text-white mb-3">Why spend is GREEN</h3>
              <p className="text-brand-200 text-sm">
                Projected 2025/26 capital spend of £2.7m exceeds the DfT allocation of £1.479m. Resurfacing is planned
                at 11.8 miles (9 miles non-principal plus 2.8 miles principal), with 54% of the budget allocated to
                planned maintenance.
              </p>
            </div>
            <div className="bg-rose-500/20 rounded-xl p-6 border border-rose-500/30">
              <h3 className="font-semibold text-white mb-3">Why condition stays RED</h3>
              <p className="text-brand-200 text-sm">
                Aggregate investment has not reversed the BCU deterioration trend. Reactive spending overtook prevention
                again in 2024/25 (52% vs 48%). The council acknowledges that roads in poor condition are susceptible to
                further deterioration through water ingress — yet planned resurfacing covers a small fraction of the
                network each year.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Inspections and Section 58 */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Inspections, Surveys and Section 58
          </h2>
          <p className="text-surface-600 mb-8">
            How Bromley says it knows the condition of its network — and where gaps appear
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Survey and inspection frequency</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• <strong>A roads:</strong> annual AI surveys via TfL LoHEG BPRN programme</li>
                <li>• <strong>B, C and U roads:</strong> annual AI-led condition surveys</li>
                <li>• <strong>Safety inspections:</strong> frequency set by road hierarchy — highest-risk roads inspected most often</li>
                <li>• <strong>Reactive repairs:</strong> raised from routine or ad-hoc inspections following public reports</li>
              </ul>
            </div>
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">How works are prioritised</h3>
              <p className="text-surface-600 text-sm mb-3">
                Planned programmes use condition scores from AI surveys, road hierarchy, reactive repair history, and
                public claims and councillor requests. The council states it has a statutory duty under the Highways Act
                (1980) to keep the highway safe — meaning reactive defect response runs alongside planned renewals.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8 mb-8">
            <h3 className="font-display text-xl font-bold mb-4">Section 41 vs Section 58</h3>
            <p className="text-brand-200 mb-4">
              Under <strong className="text-white">Section 41 of the Highways Act 1980</strong>, Bromley must maintain
              public highways. To defend a claim under <strong className="text-white">Section 58</strong>, it must show
              a reasonable system for inspecting and repairing the specific defect — not just publish network-wide
              averages or a capital budget.
            </p>
            <ul className="text-brand-300 space-y-2 text-sm">
              <li>• Was your road surveyed recently — and did the survey capture your defect?</li>
              <li>• Did the defect meet intervention criteria during routine safety inspections?</li>
              <li>• Were there prior reports (FixMyStreet, fix.bromley.gov.uk) giving actual notice?</li>
              <li>• Does photographic evidence show defect age beyond the inspection interval?</li>
            </ul>
          </div>

          <QuoteCard
            quote="Roads in poor condition are susceptible to further deterioration through water ingress that particularly weakens the layers underneath the surface, compromising the structural integrity of the road. Overall resurfacing is therefore more effective than patching small areas, to prevent water seeping through the surface."
            source="London Borough of Bromley Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* 2025/26 plans */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Planned Work 2025/26
          </h2>
          <p className="text-surface-600 mb-8">
            What Bromley says it will deliver this financial year
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-emerald-600">11.8 miles</div>
              <div className="text-sm text-emerald-800 font-medium mt-1">Total carriageway resurfacing</div>
              <div className="text-xs text-emerald-700 mt-1">9 miles non-principal + 2.8 miles principal</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">54%</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Planned maintenance share</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-amber-600">3,000–4,000</div>
              <div className="text-sm text-amber-800 font-medium mt-1">Potholes expected</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">£2.7m</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Projected capital spend</div>
              <div className="text-xs text-surface-500 mt-1">vs £1.479m DfT allocation</div>
            </div>
          </div>

          <QuoteCard
            quote="During this financial year 2025/26, we plan on resurfacing 9 miles (14.53 km) of carriageway on non-principal roads and 2.8 miles (4.5 km) of principal roads. Town centres, bus routes and access to emergency services and schools will improve thanks to the planned works."
            source="London Borough of Bromley Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* Balanced claims section */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against an AMBER-Rated Borough
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Bromley invests beyond its DfT allocation — but published condition data shows accelerating decline on residential roads
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-emerald-50 rounded-2xl p-6 border border-emerald-200">
              <h3 className="font-semibold text-emerald-900 mb-4">What works in the council's favour</h3>
              <ul className="text-emerald-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — capital spend exceeds DfT allocation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Annual AI condition surveys on all road classes
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Risk-based safety inspection hierarchy with documented response times
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Resurfacing programme recovering — 11.8 miles planned for 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A-road major deterioration improved slightly to 28.8% in 2024/25
                </li>
              </ul>
              <p className="text-emerald-700 text-sm mt-4 italic">
                Expect the council to cite GREEN spend and its asset management framework in a Section 58 defence.
              </p>
            </div>

            <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6">
              <h3 className="font-semibold text-rose-900 mb-4">What works in yours</h3>
              <ul className="text-rose-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> RED condition scorecard from DfT
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> DfT flags incomplete road condition data for Bromley
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> BCU major deterioration at 34.3% — up 67% relative from 20.6% in two years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Zero surfacing budget and 9% prevention in 2021/22 — documented by the council
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Pothole count fell because defects exceeded 1m² — council's own explanation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> ~186.6 miles in major deterioration vs 11.8 miles planned resurfacing
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a borough with GREEN spend but RED condition, your claim lives or dies on the{' '}
              <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole — proof of actual notice beyond network surveys</li>
              <li>• Photos showing defect size, depth and age (weathered edges, previous patching)</li>
              <li>• Road class — on a B, C or U road, cite the 34.3% major-deterioration rate and your street's place in the survey</li>
              <li>• Whether the defect exceeded 1m² — and was therefore excluded from pothole counts</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: prior-report search, photo assessment, and citations from Bromley's own
              transparency data — including the zero-budget period and pothole definition — where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-12 bg-surface-100 border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-3">
            Report a Pothole to Bromley Council
          </h2>
          <p className="text-surface-600 mb-6">
            Reporting a defect creates a record the council had notice. Do this before claiming — and tell us when you
            reported it so we can reference it in your pack.
          </p>
          <a
            href="https://fix.bromley.gov.uk/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-6 py-3 bg-brand-600 text-white font-semibold rounded-xl hover:bg-brand-700 transition-colors"
          >
            Report a problem — fix.bromley.gov.uk
            <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
          <p className="text-surface-500 text-sm mt-4">
            Immediate danger (large or deep pothole): call{' '}
            <a href="tel:03003038671" className="text-brand-600 hover:underline">0300 303 8671</a> out of hours.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Bromley?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            RED condition on residential roads demands a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No zero-budget 2021–23 context</li>
                <li>• No 1m² pothole definition argument</li>
                <li>• No prior-report search</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ £10m front-load and zero surfacing budget documented</li>
                <li>✅ 34.3% BCU major deterioration cited</li>
                <li>✅ Pothole definition and 2024/25 count drop explained</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Bromley</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=bromley"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Bromley Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            {faqs.map((faq) => (
              <div key={faq.question} className="bg-white rounded-xl p-6 border border-surface-200">
                <h3 className="font-semibold text-brand-900 mb-2">{faq.question}</h3>
                <p className="text-surface-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.bromley.gov.uk/roads-highways-pavements/local-highways-maintenance-transparency-report"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              London Borough of Bromley Local Highways Maintenance Transparency Report (June 2025)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
